import React, { useState, useEffect, useMemo, useRef } from "react";
import invoke from "../../common/utils/invoke";
import "./OurFundsForm.scss";
import BrandLoader from "../../common/components/BrandLoader";

const TOP_FUNDS_TAB = "Top Funds";

const OurFundsForm = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(TOP_FUNDS_TAB);

  const tabsRef = useRef(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);
  const checkScroll = () => {
    const el = tabsRef.current;
    if (!el) return;

    const isOverflowing = el.scrollWidth > el.clientWidth;

    setShowLeft(el.scrollLeft > 0);
    setShowRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);

    if (!isOverflowing) {
      setShowLeft(false);
      setShowRight(false);
    }
  };

  const scrollTabs = (direction) => {
    if (!tabsRef.current) return;

    const scrollAmount = 200;

    tabsRef.current.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  const tabs = useMemo(() => {
    if (!items.length) return [TOP_FUNDS_TAB];
    const categories = [
      ...new Set(items.map((f) => f.fundCategory).filter(Boolean)),
    ].sort();
    return [TOP_FUNDS_TAB, ...categories];
  }, [items]);

useEffect(() => {
  const el = tabsRef.current;
  if (!el) return;

  checkScroll();

  el.addEventListener("scroll", checkScroll);
  window.addEventListener("resize", checkScroll);

  return () => {
    el.removeEventListener("scroll", checkScroll);
    window.removeEventListener("resize", checkScroll);
  };
}, [tabs]);


  const filteredItems = useMemo(() => {
    if (activeTab === TOP_FUNDS_TAB) {
      return items.filter((f) => f.partOfTopFunds === true);
    }
    return items.filter((f) => f.fundCategory === activeTab);
  }, [items, activeTab]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    invoke
      .fetchOurFundsMasters()
      .then((data) => {
        if (!cancelled) {
          setItems(Array.isArray(data) ? data : []);
        }
      })
      .catch((e) => {
        if (!cancelled) setError(e?.message || "Failed to load funds");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="loader-overlay">
        <BrandLoader color="#97262A" loading={loading} size={15} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="our-funds">
        <div className="our-funds__error">{error}</div>
      </div>
    );
  }

  return (
    <div className="our-funds">
      <div className="our-funds__warper">
        {showLeft && (
          <button
            className="our-funds__arrow left"
            onClick={() => scrollTabs("left")}
          >
            ❮
          </button>
        )}
        <div className="our-funds__tabs" ref={tabsRef}>
          {tabs.map((tab) => (
            <button
              key={tab}
              type="button"
              className={`our-funds__tab ${activeTab === tab ? "active" : ""}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </button>
          ))}
        </div>
        {showRight && (
          <button
            className="our-funds__arrow right"
            onClick={() => scrollTabs("right")}
          >
            ❯
          </button>
        )}
      </div>

      {filteredItems.length === 0 ? (
        <div className="our-funds__empty">No funds in this category.</div>
      ) : (
        <div className="our-funds__grid">
          {filteredItems.map((fund) => (
            <div key={fund.id} className="our-funds__card">
              <div className="our-funds__card-header-wraper">
                <div className="our-funds__card-name">{fund.fundName}</div>
                <div className="our-funds__card-desc">
                  {fund.fundDescription}
                </div>
              </div>
              <div className="our-funds__card-wraper">
                <div className="our-funds__card-meta">
                  <span>{fund.fundPublishDate}</span>
                  <span>{fund.fundInceptionDate}</span>
                </div>
                <div className="our-funds__card-value">{fund.fundValue}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default OurFundsForm;
